<script setup>
/* eslint-disable no-use-before-define, no-unused-vars */
import { computed, ref, watch } from 'vue';
import ContactAPI from 'dashboard/api/contacts';
import {
  CLICK_ID_LABELS,
  ALL_CLICK_ID_KEYS,
  buildTouchFields,
  truncateStr as truncate,
  sourceIcon,
  platformIcon,
  formatAttrDate,
} from 'dashboard/routes/dashboard/crm/composables/useAttribution';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
});

// --- Journey timeline ---
const journeyExpanded = ref(false);
const touchpoints = ref([]);
const loadingTouchpoints = ref(false);

const contactId = computed(() => props.contact?.id || props.contact?.contactId);

async function fetchTouchpoints() {
  if (!contactId.value || touchpoints.value.length > 0) return;
  loadingTouchpoints.value = true;
  try {
    const { data } = await ContactAPI.getTouchpoints(contactId.value);
    touchpoints.value = data?.payload || [];
  } catch {
    touchpoints.value = [];
  } finally {
    loadingTouchpoints.value = false;
  }
}

watch(journeyExpanded, val => {
  if (val) fetchTouchpoints();
});

const firstTouch = computed(
  () =>
    props.contact?.first_touch_attribution ||
    props.contact?.firstTouchAttribution ||
    {}
);
const latestTouch = computed(
  () =>
    props.contact?.latest_touch_attribution ||
    props.contact?.latestTouchAttribution ||
    {}
);

const legacyIp = computed(
  () =>
    props.contact?.additional_attributes?.created_at_ip ||
    props.contact?.additionalAttributes?.created_at_ip ||
    null
);

const isDifferentTouch = computed(() => {
  return (
    Object.keys(latestTouch.value).length > 0 &&
    JSON.stringify(latestTouch.value) !== JSON.stringify(firstTouch.value)
  );
});

const getClickIdValue = (attribution, key) => {
  const ids = attribution?.click_ids;
  if (!ids || typeof ids !== 'object') return null;
  return ids[key] || null;
};

const firstTouchFields = computed(() => buildTouchFields(firstTouch.value));
const latestTouchFields = computed(() => buildTouchFields(latestTouch.value));

const firstTouchClickIds = computed(() =>
  ALL_CLICK_ID_KEYS.map(key => ({
    key,
    label: CLICK_ID_LABELS[key],
    value: getClickIdValue(firstTouch.value, key),
  }))
);
const latestTouchClickIds = computed(() =>
  ALL_CLICK_ID_KEYS.map(key => ({
    key,
    label: CLICK_ID_LABELS[key],
    value: getClickIdValue(latestTouch.value, key),
  }))
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="text-sm space-y-5">
    <!-- ===== FIRST TOUCH ===== -->
    <div>
      <div
        class="flex items-center gap-1.5 mb-3 text-xs font-medium text-n-slate-11 uppercase tracking-wide"
      >
        <span class="i-lucide-footprints size-3.5" />
        <span>Primeiro contato</span>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5"
      >
        <div v-for="(field, idx) in firstTouchFields" :key="'ft-' + idx">
          <div
            class="flex items-center gap-1 text-[10px] text-n-slate-9 uppercase tracking-wider mb-0.5"
          >
            <span :class="field.icon" class="size-3 shrink-0" />
            <span>{{ field.label }}</span>
          </div>
          <p
            class="text-xs !mb-0 break-words"
            :class="
              field.value
                ? 'text-n-slate-12 font-medium'
                : 'text-n-slate-8 italic'
            "
            :title="field.truncateValue && field.value ? field.value : ''"
          >
            {{ field.value || '--' }}
          </p>
        </div>
      </div>

      <!-- Click IDs -->
      <div class="mt-3 pt-2 border-t border-n-weak">
        <div
          class="text-[10px] text-n-slate-9 uppercase tracking-wider mb-2 flex items-center gap-1"
        >
          <span class="i-lucide-mouse-pointer-click size-3" />
          Click IDs
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
          <div v-for="cid in firstTouchClickIds" :key="'ft-cid-' + cid.key">
            <span class="text-[10px] text-n-slate-9">{{ cid.label }}</span>
            <p
              class="text-xs font-mono !mb-0 truncate"
              :class="
                cid.value
                  ? 'text-n-slate-11'
                  : 'text-n-slate-8 italic font-sans'
              "
              :title="cid.value || ''"
            >
              {{ cid.value ? truncate(cid.value, 24) : '--' }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== LATEST TOUCH ===== -->
    <div v-if="isDifferentTouch" class="pt-3 border-t border-n-weak">
      <div
        class="flex items-center gap-1.5 mb-3 text-xs font-medium text-n-slate-11 uppercase tracking-wide"
      >
        <span class="i-lucide-repeat size-3.5" />
        <span>Ultimo contato</span>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5"
      >
        <div v-for="(field, idx) in latestTouchFields" :key="'lt-' + idx">
          <div
            class="flex items-center gap-1 text-[10px] text-n-slate-9 uppercase tracking-wider mb-0.5"
          >
            <span :class="field.icon" class="size-3 shrink-0" />
            <span>{{ field.label }}</span>
          </div>
          <p
            class="text-xs !mb-0 break-words"
            :class="
              field.value
                ? 'text-n-slate-12 font-medium'
                : 'text-n-slate-8 italic'
            "
            :title="field.truncateValue && field.value ? field.value : ''"
          >
            {{ field.value || '--' }}
          </p>
        </div>
      </div>

      <!-- Click IDs -->
      <div class="mt-3 pt-2 border-t border-n-weak">
        <div
          class="text-[10px] text-n-slate-9 uppercase tracking-wider mb-2 flex items-center gap-1"
        >
          <span class="i-lucide-mouse-pointer-click size-3" />
          Click IDs
        </div>
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
          <div v-for="cid in latestTouchClickIds" :key="'lt-cid-' + cid.key">
            <span class="text-[10px] text-n-slate-9">{{ cid.label }}</span>
            <p
              class="text-xs font-mono !mb-0 truncate"
              :class="
                cid.value
                  ? 'text-n-slate-11'
                  : 'text-n-slate-8 italic font-sans'
              "
              :title="cid.value || ''"
            >
              {{ cid.value ? truncate(cid.value, 24) : '--' }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== LEGACY IP (fallback for old contacts) ===== -->
    <div
      v-if="legacyIp && !firstTouch.ip_address && !latestTouch.ip_address"
      class="pt-3 border-t border-n-weak"
    >
      <div
        class="flex items-center gap-1 text-[10px] text-n-slate-9 uppercase tracking-wider mb-0.5"
      >
        <span class="i-lucide-wifi size-3 shrink-0" />
        <span>IP (legado)</span>
      </div>
      <p class="text-xs text-n-slate-12 font-medium !mb-0">
        {{ legacyIp }}
      </p>
    </div>

    <!-- ===== JOURNEY TIMELINE ===== -->
    <div class="pt-3 border-t border-n-weak">
      <button
        class="flex items-center gap-1.5 w-full text-left text-xs font-medium text-n-slate-11 uppercase tracking-wide hover:text-n-slate-12 transition-colors"
        @click="journeyExpanded = !journeyExpanded"
      >
        <span class="i-lucide-route size-3.5" />
        <span>Jornada Completa</span>
        <span
          class="size-3.5 transition-transform ml-auto"
          :class="[
            journeyExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down',
          ]"
        />
      </button>
      <div v-if="journeyExpanded" class="mt-3">
        <div
          v-if="loadingTouchpoints"
          class="flex items-center justify-center py-4"
        >
          <span class="i-lucide-loader-2 size-4 animate-spin text-n-slate-9" />
        </div>
        <div
          v-else-if="touchpoints.length === 0"
          class="text-xs text-n-slate-8 italic py-2"
        >
          Nenhum touchpoint registrado.
        </div>
        <div v-else class="relative pl-5">
          <!-- Vertical line -->
          <div class="absolute left-[7px] top-1 bottom-1 w-px bg-n-weak" />
          <div
            v-for="(tp, idx) in touchpoints"
            :key="tp.id"
            class="relative pb-4 last:pb-0"
          >
            <!-- Dot -->
            <div
              class="absolute -left-5 top-0.5 size-3.5 rounded-full border-2 border-n-solid-2"
              :class="idx === 0 ? 'bg-woot-500' : 'bg-n-slate-8'"
            />
            <!-- Content -->
            <div class="flex items-start gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5 mb-0.5">
                  <span
                    :class="
                      tp.platform
                        ? platformIcon(tp.platform)
                        : sourceIcon(tp.utm_source)
                    "
                    class="size-3 text-n-slate-10 shrink-0"
                  />
                  <span
                    v-if="tp.platform"
                    class="text-xs font-medium text-n-slate-12 capitalize"
                  >
                    {{ tp.platform }}
                  </span>
                  <span
                    class="text-xs"
                    :class="
                      tp.platform
                        ? 'text-n-slate-9'
                        : 'font-medium text-n-slate-12'
                    "
                  >
                    {{ tp.utm_source || tp.channel || 'Direto' }}
                  </span>
                  <span v-if="tp.utm_medium" class="text-[10px] text-n-slate-9">
                    / {{ tp.utm_medium }}
                  </span>
                </div>
                <div
                  v-if="tp.utm_campaign"
                  class="text-[10px] text-n-slate-9 truncate"
                  :title="tp.utm_campaign"
                >
                  {{ tp.utm_campaign }}
                </div>
                <div
                  v-if="tp.landing_page_url"
                  class="text-[10px] text-n-slate-8 truncate"
                  :title="tp.landing_page_url"
                >
                  {{ truncate(tp.landing_page_url, 50) }}
                </div>
              </div>
              <span
                class="text-[10px] text-n-slate-9 whitespace-nowrap shrink-0"
              >
                {{ formatAttrDate(tp.created_at) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
